<template>
  <div class="dccmp">
    <main v-if="data" class="cmp-wrap">
      <!-- thesis -->
      <header class="cmp-head">
        <span class="eyebrow"><i class="dot"></i>The difference, on one screen</span>
        <h1>The same call.<br /><span class="fade">Two ways to hand it over.</span></h1>
        <p class="sub">
          Every meeting tool gives you a file and, if you are lucky, a transcript. Decast hands back the
          understanding: how the room read, what was decided, and proof it happened. Here is one real
          {{ rec.durationLabel }} call, treated both ways, from the exact same recording.
        </p>
        <div class="src">
          <b>{{ event.title }}</b><span class="sep">·</span>
          <span class="mono">{{ event.dateLabel }}</span><span class="sep">·</span>
          <span class="mono">{{ rec.durationLabel }} recording</span>
        </div>
      </header>

      <!-- scorecard -->
      <section class="score" aria-label="What each treatment gives you">
        <div class="cell head corner">The metric</div>
        <div class="cell head raw"><span class="ht">Raw recording</span><span class="hs">the export</span></div>
        <div class="cell head dc"><span class="ht">Decast brief</span><span class="hs">the same call</span></div>
        <template v-for="(r, i) in scoreRows">
          <div :key="'m' + i" class="cell"><div class="metric">{{ r.metric }}</div></div>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div :key="'a' + i" class="cell"><div class="v raw" v-html="r.raw"></div></div>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div :key="'b' + i" class="cell"><div class="v dc" v-html="r.dc"></div></div>
        </template>
      </section>

      <!-- toggle -->
      <div class="toggle">
        <div class="seg" role="group" aria-label="Choose view">
          <button v-for="v in views" :key="v.k" type="button" :aria-pressed="view === v.k ? 'true' : 'false'" @click="view = v.k">
            {{ v.label }}
          </button>
        </div>
        <span class="hint">{{ viewHint }}</span>
      </div>

      <!-- panels -->
      <div class="stage" :data-view="view">
        <!-- RAW -->
        <article class="panel p-raw">
          <div class="p-head"><span class="p-tag">Raw recording</span><span class="p-note">what most tools give you</span></div>
          <div class="p-body">
            <div class="screen" role="img" aria-label="A video file with no structure">
              <span class="play" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M7 5v14l11-7z" /></svg></span>
              <span class="stamp mono">{{ rec.durationLabel }}</span>
            </div>
            <div class="rawmeta">
              <span>{{ event.title }}</span><span class="sep">·</span>
              <span>recording.mp4 · {{ (rec.media.video && rec.media.video.size) || 'n/a' }}</span>
            </div>
            <div class="rawbar">
              <span class="rawbtn"><svg viewBox="0 0 24 24"><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" /></svg>Download</span>
              <span class="rawbtn dead" aria-disabled="true"><svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>Search (Ctrl-F only)</span>
            </div>
            <div class="rawlabel">Transcript<span>{{ wordCount.toLocaleString() }} words, unstructured</span></div>
            <div class="wall" tabindex="0" aria-label="Unstructured transcript">
              <p v-for="(c, i) in rec.transcript" :key="i"><span class="tc mono">{{ c.t }}</span>&nbsp;&nbsp;{{ c.text }}</p>
            </div>
          </div>
        </article>

        <!-- BRIEF -->
        <article class="panel p-brief">
          <div class="p-head"><span class="p-tag">Decast brief</span><span class="p-note">{{ findings.length }} findings · auto-generated</span></div>
          <div class="p-body">
            <div class="b-sent">
              <span class="lab">Sentiment</span>
              <p class="b-lead">{{ caption }}</p>
              <div class="sbar" role="img" :aria-label="caption">
                <i v-for="s in sentSegs" :key="s.k" :style="{ width: s.pct + '%', background: s.color }"></i>
              </div>
              <div class="sleg">
                <span v-for="s in sentSegs" :key="s.k"><i class="sdot" :style="{ background: s.color }"></i>{{ s.label }} <b class="tnum">{{ s.pct }}%</b></span>
              </div>
            </div>
            <div class="b-stats">
              <div v-for="st in stats" :key="st.l" class="stat"><div class="n tnum">{{ st.n }}</div><div class="l">{{ st.l }}</div></div>
            </div>
            <div class="b-h">Key insights</div>
            <ul class="insights">
              <li v-for="(x, i) in keyInsights" :key="i">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 18h6M10 21h4M12 3a6 6 0 00-4 10.5c.6.6 1 1.3 1 2.5h6c0-1.2.4-1.9 1-2.5A6 6 0 0012 3z" /></svg>
                <span>{{ x }}</span>
              </li>
            </ul>
            <div class="b-h">Every moment, timestamped</div>
            <div class="chapters">
              <div v-for="(f, i) in findings" :key="i" class="chap">
                <span class="ct mono">{{ f.timestamp }}</span>
                <span class="cs" :style="{ background: sentColor(f.sentiment) }"></span>
                <span class="cx">{{ f.text }}<span v-if="hasQuote(f)" class="cq">“{{ f.quote }}”</span></span>
              </div>
            </div>
            <div v-if="provenance" class="seal">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l7 3v6c0 4.2-2.9 7.8-7 9-4.1-1.2-7-4.8-7-9V6z" /><path d="M9 12l2 2 4-4" /></svg>
              <div><div class="st">Content Credentials</div><div class="ss mono">{{ (provenance.algorithm || 'es256').toUpperCase() }} · issued by {{ provenance.issuer }}</div></div>
            </div>
          </div>
        </article>
      </div>

      <footer class="cmp-foot">
        <p>Both panels are generated automatically from one recording. The raw side is exactly what you would
          export from a typical meeting platform. The brief is what Decast produces with no operator in the loop.</p>
        <span class="brand mono">Prepared by <b>Decast</b></span>
      </footer>
    </main>

    <div v-else-if="error" class="cmp-msg">This comparison could not be loaded.</div>
    <div v-else class="cmp-msg">Loading…</div>
  </div>
</template>

<script>
import { fetchEvent, sentimentCaption, hasQuote } from '../useEvent';

const SENT = {
  positive: ['Positive', '#7FD9B4'],
  neutral: ['Neutral', '#7A8194'],
  negative: ['Negative', '#F0997A'],
  mixed: ['Mixed', '#B49BD8'],
};

// Which event this page demonstrates. Overridable via ?id= so any brief can be shown.
const DEFAULT_EVENT = 'creative-shift-2026-09-04';

export default {
  name: 'ComparePage',
  data() {
    return { data: null, error: false, view: 'split' };
  },
  computed: {
    event() {
      return this.data.event;
    },
    rec() {
      return this.data.recordings[0];
    },
    dashboard() {
      return this.data.dashboard || {};
    },
    analysis() {
      return this.rec.analysis || {};
    },
    findings() {
      return this.analysis.findings || [];
    },
    keyInsights() {
      return (this.analysis.keyInsights || []).slice(0, 5);
    },
    provenance() {
      return this.rec.provenance || null;
    },
    wordCount() {
      return (this.rec.transcript || []).reduce((a, c) => a + (c.text || '').split(/\s+/).length, 0);
    },
    caption() {
      return sentimentCaption(this.dashboard.sentiment);
    },
    sentSegs() {
      const s = this.dashboard.sentiment || {};
      const total = Object.values(s).reduce((a, b) => a + b, 0) || 1;
      return ['positive', 'neutral', 'negative', 'mixed']
        .filter((k) => s[k])
        .map((k) => ({ k, label: SENT[k][0], color: SENT[k][1], pct: Math.round((s[k] / total) * 100) }));
    },
    stats() {
      const q = this.dashboard.qapo || {};
      return [
        { n: this.findings.length, l: 'signals' },
        { n: q.A || 0, l: 'answers & decisions' },
        { n: q.P || 0, l: 'pain points' },
      ];
    },
    scoreRows() {
      const clips = (this.rec.elements && this.rec.elements.clips ? this.rec.elements.clips.length : 0);
      return [
        { metric: 'To first insight', raw: '<span class="big">' + this.minutes + '</span> min of scrubbing', dc: '<span class="big">0</span> sec' },
        { metric: 'What you get', raw: '1 video file', dc: this.findings.length + ' findings, ' + clips + ' clips' },
        { metric: 'How it read', raw: 'watch and guess', dc: this.caption.replace(/\.$/, '').toLowerCase() },
        { metric: 'Searchable', raw: 'Ctrl-F on a wall', dc: this.wordCount.toLocaleString() + ' words, indexed' },
        { metric: 'Proof it is real', raw: 'none', dc: this.provenance ? 'signed · ' + this.provenance.issuer : 'none' },
      ];
    },
    minutes() {
      const m = String(this.rec.durationLabel || '').match(/(\d+)\s*h/);
      const mm = String(this.rec.durationLabel || '').match(/(\d+)\s*m/);
      return (m ? parseInt(m[1], 10) * 60 : 0) + (mm ? parseInt(mm[1], 10) : 0) || 74;
    },
    views() {
      return [
        { k: 'raw', label: 'Raw file' },
        { k: 'split', label: 'Side by side' },
        { k: 'brief', label: 'Decast brief' },
      ];
    },
    viewHint() {
      return { raw: 'The export, on its own', split: 'Same source recording, two treatments', brief: 'What Decast makes from it' }[this.view];
    },
  },
  created() {
    document.title = 'The difference Decast makes';
    const id = this.$route.query.id || DEFAULT_EVENT;
    fetchEvent(id)
      .then((d) => {
        this.data = d;
      })
      .catch(() => {
        this.error = true;
      });
  },
  methods: {
    hasQuote,
    sentColor(s) {
      return (SENT[String(s || 'neutral').toLowerCase()] || SENT.neutral)[1];
    },
  },
};
</script>

<style lang="scss" scoped>
.dccmp {
  --bg: #0b0c10; --panel: #14151c; --panel-2: #1a1c25; --raw: #111217;
  --line: #ffffff14; --line-soft: #ffffff0a;
  --gold: #d7df23; --ink: #f5f6fa; --ink-2: #ffffffb3; --ink-3: #ffffff70; --ink-4: #ffffff45;
  --font: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  --mono: 'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  --e: cubic-bezier(0.2, 0.7, 0.2, 1);

  background: var(--bg);
  color: var(--ink);
  min-height: 100vh;
  padding: 0 clamp(16px, 4vw, 56px);
  font-family: var(--font);
  line-height: 1.55;

  * { box-sizing: border-box; margin: 0; padding: 0; }
  .mono { font-family: var(--mono) !important; }
  .tnum { font-variant-numeric: tabular-nums; }
  button { font: inherit; color: inherit; background: none; border: 0; cursor: pointer; }
  ul { list-style: none; }
  :focus-visible { outline: 2px solid var(--gold); outline-offset: 3px; border-radius: 4px; }
  @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }

  .cmp-wrap { max-width: 1240px; margin: 0 auto; padding-block: 52px 96px; }
  .cmp-msg { max-width: 1240px; margin: 0 auto; padding: 80px 0; color: var(--ink-3); font-family: var(--mono); }

  .eyebrow { font-family: var(--mono); font-size: 11px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--ink-3); display: inline-flex; align-items: center; gap: 9px; }
  .eyebrow .dot { width: 6px; height: 6px; border-radius: 50%; background: var(--gold); }
  h1 { margin-top: 20px; font-size: clamp(2rem, 5.4vw, 3.4rem); line-height: 1.05; letter-spacing: -0.03em; font-weight: 800; max-width: 20ch; }
  h1 .fade { color: var(--ink-3); }
  .sub { margin-top: 20px; max-width: 60ch; font-size: clamp(1rem, 1.4vw, 1.15rem); color: var(--ink-2); line-height: 1.6; }
  .src { margin-top: 22px; font-family: var(--mono); font-size: 12.5px; color: var(--ink-3); display: flex; flex-wrap: wrap; gap: 8px 16px; align-items: center; }
  .src b { color: var(--ink-2); font-weight: 500; }
  .src .sep { color: var(--ink-4); }

  .score { margin-top: 44px; border: 1px solid var(--line); border-radius: 16px; overflow: hidden; display: grid; grid-template-columns: 1.2fr 1fr 1.1fr; background: var(--panel); }
  .cell { padding: 22px; border-right: 1px solid var(--line); border-bottom: 1px solid var(--line); }
  .cell:nth-child(3n) { border-right: 0; }
  .cell.head { background: var(--panel-2); padding-block: 14px; }
  .cell.corner { font-family: var(--mono); font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--ink-4); }
  .cell.head.raw { color: var(--ink-3); } .cell.head.dc { color: var(--gold); }
  .cell.head .ht { font-size: 13px; font-weight: 600; }
  .cell.head .hs { display: block; margin-top: 2px; font-family: var(--mono); font-size: 10.5px; letter-spacing: 0.1em; text-transform: uppercase; opacity: 0.75; }
  .metric { font-family: var(--mono); font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--ink-3); }
  .v { margin-top: 9px; font-size: 15px; font-weight: 500; line-height: 1.4; }
  .v.raw { color: var(--ink-3); } .v.dc { color: var(--ink); }
  .v ::v-deep .big { font-size: 26px; font-weight: 800; letter-spacing: -0.02em; }
  .v.dc ::v-deep .big { color: var(--gold); }
  .cell:nth-last-child(-n + 3) { border-bottom: 0; }

  .toggle { margin: 52px 0 22px; display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
  .seg { display: inline-flex; padding: 4px; border: 1px solid var(--line); border-radius: 11px; background: var(--panel); }
  .seg button { padding: 8px 16px; border-radius: 8px; font-size: 13.5px; font-weight: 500; color: var(--ink-3); transition: color 0.18s var(--e), background 0.18s var(--e); }
  .seg button[aria-pressed='true'] { background: var(--panel-2); color: var(--ink); }
  .seg button:hover { color: var(--ink); }
  .toggle .hint { font-family: var(--mono); font-size: 11.5px; color: var(--ink-4); }

  .stage { display: grid; gap: 20px; grid-template-columns: 1fr 1fr; align-items: start; }
  .stage[data-view='raw'], .stage[data-view='brief'] { grid-template-columns: 1fr; }
  .stage[data-view='raw'] .p-brief { display: none; }
  .stage[data-view='brief'] .p-raw { display: none; }

  .panel { border: 1px solid var(--line); border-radius: 16px; overflow: hidden; min-width: 0; }
  .panel.p-raw { background: var(--raw); }
  .panel.p-brief { background: var(--panel); border-color: #d7df2340; }
  .p-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 16px 20px; border-bottom: 1px solid var(--line); }
  .p-tag { font-family: var(--mono); font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; }
  .p-raw .p-tag { color: var(--ink-3); } .p-brief .p-tag { color: var(--gold); }
  .p-note { font-family: var(--mono); font-size: 11px; color: var(--ink-4); }
  .p-body { padding: 20px; }

  .screen { aspect-ratio: 16 / 9; border-radius: 10px; background: radial-gradient(120% 90% at 50% 40%, #20222b 0%, #0e0f14 78%); display: grid; place-items: center; position: relative; border: 1px solid var(--line-soft); }
  .screen .play { width: 56px; height: 56px; border-radius: 50%; background: #ffffff12; display: grid; place-items: center; border: 1px solid #ffffff20; }
  .screen .play svg { width: 22px; height: 22px; fill: var(--ink-3); margin-left: 3px; }
  .screen .stamp { position: absolute; left: 12px; bottom: 12px; font-family: var(--mono); font-size: 11px; color: var(--ink-4); background: #00000066; padding: 3px 8px; border-radius: 5px; }
  .rawmeta { margin-top: 16px; display: flex; flex-wrap: wrap; gap: 8px 14px; align-items: center; font-family: var(--mono); font-size: 12px; color: var(--ink-3); }
  .rawmeta .sep { color: var(--ink-4); }
  .rawbar { margin-top: 16px; display: flex; gap: 10px; flex-wrap: wrap; }
  .rawbtn { padding: 9px 14px; border: 1px solid var(--line); border-radius: 9px; font-size: 13px; color: var(--ink-2); display: inline-flex; align-items: center; gap: 8px; }
  .rawbtn svg { width: 14px; height: 14px; stroke: currentColor; fill: none; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
  .rawbtn.dead { color: var(--ink-4); cursor: not-allowed; }
  .rawlabel { margin-top: 26px; font-family: var(--mono); font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--ink-4); display: flex; justify-content: space-between; align-items: baseline; }
  .rawlabel span { color: var(--ink-4); text-transform: none; letter-spacing: 0; font-size: 11px; }
  .wall { margin-top: 12px; height: 420px; overflow-y: auto; padding: 16px; border: 1px solid var(--line-soft); border-radius: 10px; background: #0c0d12; font-size: 13.5px; line-height: 1.85; color: var(--ink-3); -webkit-mask-image: linear-gradient(180deg, #000 88%, transparent); mask-image: linear-gradient(180deg, #000 88%, transparent); }
  .wall .tc { color: var(--ink-4); font-family: var(--mono); font-size: 11.5px; }
  .wall p { margin-bottom: 2px; }

  .b-sent .lab { font-family: var(--mono); font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--ink-3); }
  .b-lead { margin-top: 12px; font-size: 20px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.3; }
  .sbar { margin-top: 16px; display: flex; height: 12px; border-radius: 7px; overflow: hidden; background: var(--line); }
  .sbar i { height: 100%; }
  .sleg { margin-top: 12px; display: flex; flex-wrap: wrap; gap: 8px 16px; font-size: 12.5px; color: var(--ink-2); }
  .sleg span { display: inline-flex; align-items: center; gap: 7px; }
  .sdot { width: 8px; height: 8px; border-radius: 50%; }
  .sleg b { font-family: var(--mono); font-weight: 500; color: var(--ink-3); }
  .b-stats { margin-top: 22px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .stat { border: 1px solid var(--line); border-radius: 11px; padding: 14px; }
  .stat .n { font-family: var(--mono); font-size: 24px; font-weight: 600; letter-spacing: -0.02em; color: var(--gold); }
  .stat .l { margin-top: 3px; font-size: 12px; color: var(--ink-3); }
  .b-h { margin-top: 26px; font-family: var(--mono); font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--ink-3); }
  .insights { margin-top: 12px; display: flex; flex-direction: column; gap: 10px; }
  .insights li { display: flex; gap: 10px; font-size: 14px; color: var(--ink-2); line-height: 1.5; }
  .insights svg { width: 15px; height: 15px; flex: 0 0 15px; margin-top: 3px; stroke: var(--gold); fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
  .chapters { margin-top: 12px; height: 300px; overflow-y: auto; display: flex; flex-direction: column; gap: 2px; padding-right: 4px; }
  .chap { display: grid; grid-template-columns: auto auto 1fr; gap: 10px; align-items: baseline; padding: 9px 8px; border-radius: 8px; transition: background 0.15s var(--e); }
  .chap:hover { background: var(--panel-2); }
  .chap .ct { font-family: var(--mono); font-size: 11.5px; color: var(--gold); white-space: nowrap; }
  .chap .cs { width: 7px; height: 7px; border-radius: 50%; transform: translateY(4px); }
  .chap .cx { font-size: 13px; color: var(--ink-2); line-height: 1.45; min-width: 0; }
  .chap .cq { display: block; margin-top: 3px; font-size: 12px; color: var(--ink-3); font-style: italic; border-left: 2px solid var(--line); padding-left: 8px; }
  .seal { margin-top: 22px; display: flex; align-items: center; gap: 12px; padding: 14px 16px; border: 1px solid #d7df2340; border-radius: 11px; background: #d7df230a; }
  .seal svg { width: 22px; height: 22px; stroke: var(--gold); fill: none; stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round; flex: 0 0 22px; }
  .seal .st { font-size: 13px; color: var(--ink); font-weight: 600; }
  .seal .ss { font-family: var(--mono); font-size: 11px; color: var(--ink-3); margin-top: 2px; }

  .cmp-foot { margin-top: 64px; padding-top: 26px; border-top: 1px solid var(--line); display: flex; justify-content: space-between; align-items: baseline; gap: 16px; flex-wrap: wrap; }
  .cmp-foot p { font-size: 13.5px; color: var(--ink-3); max-width: 56ch; line-height: 1.6; }
  .cmp-foot .brand { font-size: 12px; letter-spacing: 0.06em; color: var(--ink-4); }
  .cmp-foot .brand b { color: var(--gold); font-weight: 500; }

  @media (max-width: 820px) {
    .stage { grid-template-columns: 1fr; }
  }
  @media (max-width: 560px) {
    .score { grid-template-columns: 1fr; }
    .cell { border-right: 0 !important; }
  }
}
</style>
